import React from 'react';
import { User } from '../types';
import UserAvatar from './UserAvatar';

interface UserDetailsCardProps {
  user: User;
  onAvatarClick?: (userId: string | number, userName?: string) => void;
  onOpenChat?: (chatId: string) => void;
}

export const formatDate = (dateValue: any): string => {
  if (!dateValue) return 'Не указано';

  let date: Date;

  if (dateValue instanceof Date) {
    date = dateValue;
  } else if (typeof dateValue === 'string') {
    date = new Date(dateValue);
  } else if (dateValue?.$date) {
    date = new Date(dateValue.$date);
  } else if (dateValue?.timestamp) {
    date = new Date(dateValue.timestamp);
  } else {
    date = new Date(dateValue);
  }

  if (isNaN(date.getTime())) {
    return 'Неверная дата';
  }

  return date.toLocaleString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

export const getStatusBadge = (user: User): { text: string; class: string } => {
  if (user.Blocked) return { text: 'Заблокирован', class: 'status-blocked' };
  if (user.deactivated) return { text: 'Деактивирован', class: 'status-deactivated' };
  if (user.chatNotFound) return { text: 'Чат не найден', class: 'status-not-found' };
  if (!user.send) return { text: 'Неактивен', class: 'status-inactive' };
  return { text: 'Активен', class: 'status-active' };
};

export const getUserType = (user: User): string => {
  if (user.isTeacher) return 'Учитель';
  if (user.isNewUser) return 'Новый пользователь';
  return 'Обычный пользователь';
};

const UserDetailsCard: React.FC<UserDetailsCardProps> = ({ user, onAvatarClick, onOpenChat }) => {
  const status = getStatusBadge(user);
  const userType = getUserType(user);

  return (
    <>
      <div className="user-card-header">
        <UserAvatar
          userId={user.chatId}
          userName={user.userName}
          size="extra-large"
          onClick={onAvatarClick ? () => onAvatarClick(user.chatId, user.userName) : undefined}
        />
        <div className="user-info">
          <h3 className="user-name">{user.userName || 'Без имени'}</h3>
          <span
            className={`user-type ${
              user.isTeacher ? 'type-teacher' : user.isNewUser ? 'type-new' : 'type-regular'
            }`}
          >
            {userType}
          </span>
        </div>
      </div>

      <div className="user-details">
        <div className="detail-row">
          <span className="detail-label">ID:</span>
          <span className="detail-value">{user._id}</span>
        </div>

        {user.chatId && (
          <div className="detail-row">
            <span className="detail-label">Chat ID:</span>
            <span className="detail-value">{user.chatId}</span>
          </div>
        )}

        {user.firstName && user.firstName !== 'unknown' && (
          <div className="detail-row">
            <span className="detail-label">Имя:</span>
            <span className="detail-value">{user.firstName}</span>
          </div>
        )}

        {user.lastName && user.lastName !== 'unknown' && (
          <div className="detail-row">
            <span className="detail-label">Фамилия:</span>
            <span className="detail-value">{user.lastName}</span>
          </div>
        )}

        {user.group && (
          <div className="detail-row">
            <span className="detail-label">Группа:</span>
            <span className="detail-value">{user.group}</span>
          </div>
        )}

        {user.time && (
          <div className="detail-row">
            <span className="detail-label">Время отправки:</span>
            <span className="detail-value">
              {user.time === 'when_schedule_appears' ? 'Когда появится расписание' : user.time}
            </span>
          </div>
        )}

        <div className="detail-row">
          <span className="detail-label">Статус:</span>
          <span className={`status-badge ${status.class}`}>{status.text}</span>
        </div>

        <div className="detail-row">
          <span className="detail-label">Получает сообщения:</span>
          <span className={`detail-value ${user.send ? 'status-active' : 'status-inactive'}`}>
            {user.send ? '✅ Да' : '❌ Нет'}
          </span>
        </div>

        <div className="detail-row">
          <span className="detail-label">Выпускник:</span>
          <span className={`detail-value ${user.graduated ? 'status-graduated' : 'status-active'}`}>
            {user.graduated ? '🎓 Да' : '👨‍🎓 Нет'}
          </span>
        </div>

        {user.fcmToken && (
          <div className="detail-row">
            <span className="detail-label">Мобильное приложение:</span>
            <span className="detail-value has-app">📱 Установлено</span>
          </div>
        )}

        {user.appToken && (
          <div className="detail-row">
            <span className="detail-label">App Token:</span>
            <span className="detail-value token-value">{user.appToken.substring(0, 20)}...</span>
          </div>
        )}

        {user.lastScheduleSentDate && (
          <div className="detail-row">
            <span className="detail-label">Последнее расписание:</span>
            <span className="detail-value">{user.lastScheduleSentDate}</span>
          </div>
        )}

        <div className="detail-row">
          <span className="detail-label">Создан:</span>
          <span className="detail-value">{formatDate(user.createdAt)}</span>
        </div>

        {user.updatedAt !== user.createdAt && (
          <div className="detail-row">
            <span className="detail-label">Обновлен:</span>
            <span className="detail-value">{formatDate(user.updatedAt)}</span>
          </div>
        )}

        {user.lastUpdated && user.lastUpdated !== user.updatedAt && (
          <div className="detail-row">
            <span className="detail-label">Последняя активность:</span>
            <span className="detail-value">{formatDate(user.lastUpdated)}</span>
          </div>
        )}
      </div>

      {onOpenChat && user.chatId && (
        <div className="user-card-actions">
          <button
            className="open-chat-btn"
            onClick={() => onOpenChat(String(user.chatId))}
            title="Открыть переписку с пользователем"
          >
            💬 Открыть чат
          </button>
        </div>
      )}
    </>
  );
};

export default UserDetailsCard;
